/**
 * Free-text search intent → OSM amenity / shop tags.
 * Hungarian + English keywords; leftover tokens become a name filter.
 */

export type OsmTagFilter = { key: string; value: string };

export type SearchIntent = {
  id: string;
  filters: OsmTagFilter[];
  /** Remaining query after removing the intent keyword (e.g. brand name). */
  nameHint: string;
};

type IntentDef = {
  id: string;
  keywords: string[];
  filters: OsmTagFilter[];
};

const INTENT_DEFS: IntentDef[] = [
  {
    id: 'fuel',
    keywords: [
      'benzinkut',
      'benzinkút',
      'benzin',
      'uzemanyag',
      'üzemanyag',
      'tankolas',
      'tankolás',
      'fuel',
      'petrol',
      'gas station',
      'gasstation',
    ],
    filters: [{ key: 'amenity', value: 'fuel' }],
  },
  {
    id: 'hospital',
    keywords: [
      'korhaz',
      'kórház',
      'klinika',
      'hospital',
      'clinic',
      'emergency',
      'mentoallomas',
      'mentőállomás',
    ],
    filters: [
      { key: 'amenity', value: 'hospital' },
      { key: 'amenity', value: 'clinic' },
      { key: 'healthcare', value: 'hospital' },
    ],
  },
  {
    id: 'pharmacy',
    keywords: ['gyogyszertar', 'gyógyszertár', 'patika', 'pharmacy', 'apteka'],
    filters: [{ key: 'amenity', value: 'pharmacy' }],
  },
  {
    id: 'parking',
    keywords: ['parkolo', 'parkoló', 'parking', 'parkolas', 'parkolás'],
    filters: [{ key: 'amenity', value: 'parking' }],
  },
  {
    id: 'restaurant',
    keywords: ['etterem', 'étterem', 'restaurant', 'vendeglo', 'vendéglő'],
    filters: [
      { key: 'amenity', value: 'restaurant' },
      { key: 'amenity', value: 'fast_food' },
    ],
  },
  {
    id: 'cafe',
    keywords: ['kavezo', 'kávézó', 'cafe', 'coffee', 'cukraszda', 'cukrászda'],
    filters: [
      { key: 'amenity', value: 'cafe' },
      { key: 'amenity', value: 'ice_cream' },
    ],
  },
  {
    id: 'supermarket',
    keywords: [
      'szupermarket',
      'supermarket',
      'elelmiszer',
      'élelmiszer',
      'abc',
      'grocery',
      'bevasarlas',
      'bevásárlás',
    ],
    filters: [
      { key: 'shop', value: 'supermarket' },
      { key: 'shop', value: 'convenience' },
    ],
  },
  {
    id: 'hotel',
    keywords: ['szalloda', 'szálloda', 'hotel', 'panzio', 'panzió'],
    filters: [
      { key: 'tourism', value: 'hotel' },
      { key: 'tourism', value: 'guest_house' },
    ],
  },
  {
    id: 'police',
    keywords: ['rendorseg', 'rendőrség', 'police'],
    filters: [{ key: 'amenity', value: 'police' }],
  },
  {
    id: 'atm',
    keywords: ['atm', 'bankautomata', 'penzfelvetel', 'pénzfelvétel'],
    filters: [{ key: 'amenity', value: 'atm' }],
  },
  {
    id: 'toilets',
    keywords: ['wc', 'mosdo', 'mosdó', 'toilet', 'toilets', 'restroom'],
    filters: [{ key: 'amenity', value: 'toilets' }],
  },
  {
    id: 'school',
    keywords: ['iskola', 'school', 'ovi', 'ovoda', 'óvoda', 'kindergarten'],
    filters: [
      { key: 'amenity', value: 'school' },
      { key: 'amenity', value: 'kindergarten' },
    ],
  },
];

function foldText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Detect amenity intent. Longest keyword wins.
 * Returns null for ordinary address / place queries.
 */
export function parseSearchIntent(query: string): SearchIntent | null {
  const folded = foldText(query);
  if (folded.length < 2) return null;

  let best: { def: IntentDef; keyword: string } | null = null;
  for (const def of INTENT_DEFS) {
    for (const keyword of def.keywords) {
      const needle = foldText(keyword);
      if (needle.length < 2) continue;
      const re = new RegExp(`(?:^|\\s)${escapeRegExp(needle)}(?:\\s|$)`, 'u');
      if (!re.test(folded)) continue;
      if (!best || needle.length > foldText(best.keyword).length) {
        best = { def, keyword: needle };
      }
    }
  }
  if (!best) return null;

  const nameHint = folded
    .replace(new RegExp(`(?:^|\\s)${escapeRegExp(best.keyword)}(?:\\s|$)`, 'u'), ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return {
    id: best.def.id,
    filters: best.def.filters,
    nameHint,
  };
}
